import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import {
  identifyRevenueCatUser,
  clearRevenueCatUser,
} from '@/lib/revenueCat';

type ShopRole = Database['public']['Enums']['shop_role'];

interface ShopMembership {
  id: string;
  shop_id: string;
  role: ShopRole;
  is_also_artist: boolean;
  shop: {
    id: string;
    name: string;
  };
}

interface UserProfile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ data: any; error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ data: any; error: Error | null }>;
  signOut: () => Promise<void>;
  memberships: ShopMembership[];
  membershipsLoaded: boolean;
  currentShopId: string | null;
  currentRole: ShopRole | null;
  setCurrentShop: (shopId: string) => void;
  refetchMemberships: (userId?: string) => Promise<void>;
  isOwner: boolean;
  isManager: boolean;
  isArtist: boolean;
  isApprentice: boolean;
  isPiercer: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState<ShopMembership[]>([]);
  const [membershipsLoaded, setMembershipsLoaded] = useState(false);
  const [currentShopId, setCurrentShopId] = useState<string | null>(null);

  // Fetch user profile
  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles_safe')
      .select('id, full_name, avatar_url')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
      return;
    }

    setProfile(data as UserProfile);
  };

  // Fetch user memberships
  const fetchMemberships = async (userId: string) => {
    setMembershipsLoaded(false);
    const { data, error } = await supabase
      .from('shop_memberships')
      .select(`
        id,
        shop_id,
        role,
        is_also_artist,
        shop:shops (
          id,
          name
        )
      `)
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching memberships:', error);
      setMemberships([]);
      setMembershipsLoaded(true);
      return;
    }

    const formattedMemberships = (data || []).map((m) => ({
      id: m.id,
      shop_id: m.shop_id,
      role: m.role,
      is_also_artist: m.is_also_artist ?? false,
      shop: m.shop as { id: string; name: string },
    }));

    setMemberships(formattedMemberships);
    setMembershipsLoaded(true);

    // Auto-select first shop if none selected or select newly created shop
    if (formattedMemberships.length > 0) {
      const savedShopId = localStorage.getItem('currentShopId');
      const validShop = formattedMemberships.find(m => m.shop_id === savedShopId);
      if (!validShop) {
        // No valid saved shop, select the first one (likely the newly created one)
        setCurrentShopId(formattedMemberships[0].shop_id);
        localStorage.setItem('currentShopId', formattedMemberships[0].shop_id);
      } else if (!currentShopId) {
        setCurrentShopId(validShop.shop_id);
      }
    }
  };

  // Public method to refetch memberships
  const refetchMemberships = async (userId?: string) => {
    const id = userId ?? user?.id;
    if (id) {
      await fetchMemberships(id);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setLoading(true);
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Use setTimeout to avoid potential deadlock with the auth client
        setTimeout(async () => {
          await Promise.all([
            fetchMemberships(session.user.id),
            fetchProfile(session.user.id)
          ]);
          setLoading(false);
        }, 0);
        // Mirror the Supabase identity over to RevenueCat so iOS IAP
        // entitlements key off the same UUID. No-op off iOS native.
        // Fire-and-forget — IAP state isn't on the critical signin path.
        void identifyRevenueCatUser(session.user.id);
      } else {
        setMemberships([]);
        setMembershipsLoaded(true);
        setCurrentShopId(null);
        setProfile(null);
        setLoading(false);
        // Drop the RevenueCat identity so the next user on this device
        // doesn't inherit the previous user's entitlement cache.
        void clearRevenueCatUser();
      }
    });

    // Then get initial session
    (async () => {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        await Promise.all([
          fetchMemberships(session.user.id),
          fetchProfile(session.user.id)
        ]);
        // Identify on cold-launch as well — auth listener doesn't always
        // fire for restored sessions, so we belt-and-suspenders here.
        void identifyRevenueCatUser(session.user.id);
      } else {
        setMembershipsLoaded(true);
      }

      setLoading(false);
    })();

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          full_name: fullName,
        },
      },
    });

    // Track successful signups in trial_dropoffs for the retention funnel.
    // Fires once per successful signup (not on every auth state change like
    // it used to). Silent / non-blocking — analytics failure must not prevent
    // signup from completing.
    if (!error && data?.user) {
      try {
        await supabase.from("trial_dropoffs").upsert(
          {
            user_id: data.user.id,
            email: data.user.email || email || "",
            full_name: fullName || data.user.user_metadata?.full_name || null,
          },
          { onConflict: "user_id" }
        );
      } catch {
        // Silent fail — analytics, not critical path
      }
    }

    return { data, error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setMemberships([]);
    setMembershipsLoaded(false);
    setCurrentShopId(null);
    setProfile(null);
    localStorage.removeItem('currentShopId');
  };

  const setCurrentShop = (shopId: string) => {
    setCurrentShopId(shopId);
    localStorage.setItem('currentShopId', shopId);
  };

  // Current role based on selected shop
  const currentMembership = memberships.find(m => m.shop_id === currentShopId);
  const currentRole = currentMembership?.role ?? null;

  const isOwner = currentRole === 'owner';
  const isManager = currentRole === 'manager';
  const isArtist = currentRole === 'artist';
  const isApprentice = currentRole === 'apprentice';
  const isPiercer = currentRole === 'piercer';
  const isAdmin = isOwner || isManager;

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    memberships,
    membershipsLoaded,
    currentShopId,
    currentRole,
    setCurrentShop,
    refetchMemberships,
    isOwner,
    isManager,
    isArtist,
    isApprentice,
    isPiercer,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
